import * as Sentry from "@sentry/browser";

import type { PusherChannelAndEventSubscriptionProps } from "@lib/pusher";

import type { RealtimeMessageData } from "./realtimeTypes";
import type { DustEventMeta } from "./types";

const DUST_TRANSPORT_KEYS: ReadonlySet<string> = new Set(["stream_id", "sequence", "message_id"]);

type EventCallback = (data: unknown, meta?: DustEventMeta) => void;

/**
 * Strips DUST transport metadata from the payload before dispatching to
 * callbacks. These fields are used by the sequencing layer but are not part
 * of the application-level result shape; leaking them causes strict runtime
 * validators (objectOf from @altostra/type-validations) to reject the payload.
 */
function stripTransportMetadata(payload: unknown): unknown {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
    return payload;
  }
  const record = payload as Record<string, unknown>;
  // A payload is DUST-wrapped if it carries message_id (stamped on every published
  // message) or stream_id (ordered-stream messages, plus the synthetic buffer-overflow
  // envelope, which has no message_id). A bare sequence is deliberately not sufficient
  // evidence: unlike the other two it is a plausible application field name.
  if (!("message_id" in record) && !("stream_id" in record)) {
    return payload;
  }
  const stripped: Record<string, unknown> = {};
  for (const key of Object.keys(record)) {
    if (!DUST_TRANSPORT_KEYS.has(key)) {
      stripped[key] = record[key];
    }
  }
  return stripped;
}

export class EventCallbackManager {
  private eventCallbacks: Map<string, Map<string, EventCallback[]>> = new Map();
  private exclusiveEventCallbacks: Map<string, Map<string, EventCallback[]>> = new Map();

  /** Push this callback as the current exclusive handler; previous ones stay on a stack. */
  setExclusiveCallback({ channelName, eventName, callback }: PusherChannelAndEventSubscriptionProps): void {
    let channelCallbacks = this.exclusiveEventCallbacks.get(channelName);
    if (!channelCallbacks) {
      channelCallbacks = new Map();
      this.exclusiveEventCallbacks.set(channelName, channelCallbacks);
    }
    let eventStack = channelCallbacks.get(eventName);
    if (!eventStack) {
      eventStack = [];
      channelCallbacks.set(eventName, eventStack);
    }
    const existingIndex = eventStack.indexOf(callback);
    if (existingIndex !== -1) {
      eventStack.splice(existingIndex, 1);
    }
    eventStack.push(callback);
  }

  /** Remove this callback from the stack, restoring the previous one if it was current. */
  removeExclusiveCallback({ channelName, eventName, callback }: PusherChannelAndEventSubscriptionProps): void {
    const channelCallbacks = this.exclusiveEventCallbacks.get(channelName);
    if (!channelCallbacks) {
      return;
    }
    const eventStack = channelCallbacks.get(eventName);
    if (!eventStack) {
      return;
    }
    const index = eventStack.indexOf(callback);
    if (index === -1) {
      return;
    }
    eventStack.splice(index, 1);
    if (eventStack.length === 0) {
      channelCallbacks.delete(eventName);
    }
    if (channelCallbacks.size === 0) {
      this.exclusiveEventCallbacks.delete(channelName);
    }
  }

  /**
   * Add a callback for a specific channel and event
   */
  addCallback({ channelName, eventName, callback }: PusherChannelAndEventSubscriptionProps): void {
    let channelCallbacks = this.eventCallbacks.get(channelName);
    if (!channelCallbacks) {
      channelCallbacks = new Map();
      this.eventCallbacks.set(channelName, channelCallbacks);
    }
    let eventCallbacks = channelCallbacks.get(eventName);
    if (!eventCallbacks) {
      eventCallbacks = [];
      channelCallbacks.set(eventName, eventCallbacks);
    }
    eventCallbacks.push(callback);
  }

  /**
   * Remove a specific callback for a channel and event
   */
  removeCallback({ channelName, eventName, callback }: PusherChannelAndEventSubscriptionProps): void {
    const channelCallbacks = this.eventCallbacks.get(channelName);
    if (channelCallbacks) {
      const eventCallbacks = channelCallbacks.get(eventName);
      if (eventCallbacks) {
        const index = eventCallbacks.indexOf(callback);
        if (index !== -1) {
          eventCallbacks.splice(index, 1);
        }
        // If no more callbacks for this event, remove the event entry
        if (eventCallbacks.length === 0) {
          channelCallbacks.delete(eventName);
        }
      }
      // If no more events for this channel, remove the channel entry
      if (channelCallbacks.size === 0) {
        this.eventCallbacks.delete(channelName);
      }
    }
  }

  /**
   * Check if a channel has any callbacks
   */
  hasCallbacks(channelName: string): boolean {
    const channelCallbacks = this.eventCallbacks.get(channelName);
    if (channelCallbacks && channelCallbacks.size > 0) {
      return true;
    }
    const exclusiveChannelCallbacks = this.exclusiveEventCallbacks.get(channelName);
    return exclusiveChannelCallbacks ? exclusiveChannelCallbacks.size > 0 : false;
  }

  /**
   * Process a message by calling all registered callbacks
   */
  processMessage(channelName: string, message: RealtimeMessageData): void {
    const channelCallbacks = this.eventCallbacks.get(channelName);
    const exclusiveChannelCallbacks = this.exclusiveEventCallbacks.get(channelName);
    if (!channelCallbacks && !exclusiveChannelCallbacks) {
      return;
    }

    const payload = message.payload;
    const eventName = message.event;

    if (!eventName) {
      console.error("Received message without event name:", message);
      return;
    }

    // Loudly surface envelopes with a missing payload instead of silently handing `undefined`
    // to subscribers (which tends to manifest as TypeErrors deep inside callbacks and causes
    // the real producer-side bug to be invisible). Payload is expected to be present on every
    // real event; if it's missing, the producer is buggy and we want to know about it.
    if (payload === undefined || payload === null) {
      const err = new Error(
        `EventCallbackManager received message with missing payload for event "${eventName}" on channel "${channelName}"`
      );
      console.error(err.message, message);
      Sentry.captureException(err, {
        extra: {
          channelName,
          eventName,
          message,
        },
      });
      return;
    }

    const exclusiveStack = exclusiveChannelCallbacks?.get(eventName);
    const exclusiveCallback =
      exclusiveStack && exclusiveStack.length > 0 ? exclusiveStack[exclusiveStack.length - 1] : undefined;
    const eventCallbacks = channelCallbacks?.get(eventName) ?? [];
    // Snapshot so a callback that unsubscribes during dispatch can't skip later entries.
    const callbacksToInvoke = exclusiveCallback ? [exclusiveCallback, ...eventCallbacks] : [...eventCallbacks];
    if (callbacksToInvoke.length === 0) {
      return;
    }

    const cleanPayload = stripTransportMetadata(payload);
    // Only messages that carry an id get the meta argument, so callbacks that never look at it
    // keep receiving exactly one argument.
    const rawMessageId = (payload as Record<string, unknown>).message_id;
    const meta: DustEventMeta | undefined =
      typeof rawMessageId === "string" ? { messageId: rawMessageId } : undefined;
    callbacksToInvoke.forEach((callback) => {
      try {
        if (meta) {
          callback(cleanPayload, meta);
        } else {
          callback(cleanPayload);
        }
      } catch (error) {
        console.error(`Error in callback for event ${eventName}:`, error);
        Sentry.captureException(error, {
          extra: {
            channelName,
            eventName,
            payload,
          },
        });
      }
    });
  }
}
